import moment from "moment";

import { stripHtml } from "utils/stripHtml";
import { formatEircode } from "utils/validators";

import {
  formatAllergiesToString,
  formatFamilyMedicalHistoryToString,
  formatMedicalHistoryToString,
} from "./format";

export const parseSmoker = (smoker) => {
  if (smoker === "YES") return "CURRENT_SMOKER";

  if (smoker === "NO") return "NON_SMOKER";

  if (smoker === "UNKNOWN") return "UNKNOWN";

  return "";
};

export const getPatientNextOfKin = (patient) => {
  const fields = [
    patient.nextOfKin,
    patient.nextOfKinRelationship,
    patient.nextOfKinPhone,
  ].filter(Boolean);
  return fields.join(" | ");
};

export const getGeneralInformationDefaultValues = () => ({
  hospital: "",
  referralType: "",
});

export const getProstateDefaultValues = (today) => ({
  psaTests: [],
  hasSeenUrologist: "",
  dateOfUrologyConsult: null,
  hasPreviousBiopsy: "",
  hasPreviousBiopsyHospital: "",
  hasPreviousBiopsyDate: null,
  hasPreviousBiopsyResult: "",
  dateOfBiopsy: moment(new Date(today)).format("DD-MM-YYYY"),
  digitalRectalExam: "",
  otherClinicalExam: "",
  resultOfUrinalysis: "",
  otherLabInvestigations: "",
  otherRadInvestigationsProstate: "",
  currentMedication: "",
  patientOnAnticoagulants: "",
  patientOnAspirin: "",
  patientOnPlavix: "",
  patientOnWarfarin: "",
  aspirinDetails: "",
  plavixDetails: "",
  warfarinDetails: "",
});

export const getLungsDefaultValues = () => ({
  informationForContrastCt: "",
  otherClinicalFindings: "",
  otherRadInvestigations: "",
  hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer: "No",
  otherPersistentUnexplainedSymptoms: "",
  haemoptysis: false,
  clubbing: false,
  lymphadenopathy: false,
  hepatosplenomegaly: false,
  chestSigns: false,
  historyOfAsthma: false,
  historyOfRentalInsufficiency: false,
  historyOfAllergyToContrastDye: false,
  lungLabInvestigation: "",
  specifiedChestSigns: "",
  radInvestigation1: "not-done",
  radInvestigation1Hospital: "",
  radInvestigation1Date: null,
  radInvestigation1Result: "",
  radInvestigation1Comment: "",
  radInvestigation2: "not-done",
  radInvestigation2Hospital: "",
  radInvestigation2Date: null,
  radInvestigation2Result: "",
  radInvestigation2Comment: "",
  currentMedication: "",
  patientOnAnticoagulants: "",
  patientOnAspirin: "",
  patientOnPlavix: "",
  patientOnWarfarin: "",
  aspirinDetails: "",
  plavixDetails: "",
  warfarinDetails: "",
});

export const getPatientDetailsDefaultValues = ({
  staffMember,
  patient,
  patientPreferences,
  practiceOutOfHoursPhone,
}) => ({
  // The practice's number wins: the backend puts it on the referral ahead of the form's value
  // (KIN-5), so the form starts from it and shows what will be sent (KIN-2131).
  gpOutofHoursNumber:
    practiceOutOfHoursPhone || staffMember?.outOfHoursNumber || "",
  familyName: patient?.surname || "",
  firstName: patient?.firstName || "",
  title: patient?.title || "",
  gender:
    patient?.gender.toUpperCase() === "MALE" ||
    patient?.gender.toUpperCase() === "FEMALE"
      ? patient?.gender.toUpperCase() || ""
      : "",
  mothersMaidenName: patient?.mothersMaidenName || "",
  dateOfBirth: patient?.dateOfBirth
    ? moment(new Date(patient?.dateOfBirth)).format("DD-MM-YYYY")
    : null,
  firstLanguage: patientPreferences?.mainLanguage || "eng",
  wheelchairAssistance: "",
  interpreterRequired: "",
  streetAddress: patient?.address?.addressLine1 || "",
  addressLine2: patient?.address?.addressLine2 || "",
  addressLine3: patient?.address?.addressLine3 || "",
  addressLine4: patient?.address?.city || "",
  eircode: formatEircode(patient?.address?.postCode) || "",
  mobile: patient?.mobilePhone || "",
  phone: patient?.homePhone || "",
  email: patient?.email || "",
});

// A baseline holds blood pressure twice: as "120/80" and as systolic/diastolic. Either can be missing on its
// own, so read the string when there is one and fall back to the numbers.
const baselineBloodPressure = (patientBaseline) => {
  const [systolic, diastolic] = patientBaseline?.bloodPressure?.split("/") ?? [];
  return {
    bp1: systolic?.trim() || (patientBaseline?.systolic != null ? `${patientBaseline.systolic}` : ""),
    bp2: diastolic?.trim() || (patientBaseline?.diastolic != null ? `${patientBaseline.diastolic}` : ""),
  };
};

export const getReferralFormDefaultValues = ({
  activeConsultation,
  medicalHistory,
  allergies,
  familyMedicalHistory,
  patient,
  patientSocial,
  patientBaseline,
  isProstateReferral,
  isLungsReferral,
}) => ({
  referralPriority: "",
  preferredConsultantFamilyName: "",
  preferredConsultantFirstName: "",
  reasonForReferral: "",
  historyOfPresentingComplains:
    activeConsultation?.consultationNoteList
      ?.map((note) => stripHtml(note.note).trim())
      .filter(Boolean)
      .join("\n") || "",
  previousHospitalAttendance: "",
  historyOfPastIllness:
    medicalHistory && medicalHistory?.length > 0
      ? formatMedicalHistoryToString(medicalHistory)
      : "",
  historyOfSurgicalProcedures: "",
  allergiesAdverseMedicationEvents:
    allergies && allergies?.length > 0 ? formatAllergiesToString(allergies) : "",
  relevantFamilyHistory:
    familyMedicalHistory && familyMedicalHistory?.length > 0
      ? formatFamilyMedicalHistoryToString(familyMedicalHistory)
      : "",
  additionalInformation: "",
  currentMedication: "",
  patientOnAnticoagulants: "",
  anticoagulantMedications: [{ name: "", dosage: "" }],
  nextOfKin: getPatientNextOfKin(patient) || "",
  smoker: patientSocial?.smoker ? parseSmoker(patientSocial?.smoker) : "",
  unitsOfAlcohol: patientSocial?.alcoholUnitsPerWeek
    ? `${patientSocial?.alcoholUnitsPerWeek}`
    : "",
  numberOfCigarettes: patientSocial?.smokePerDay
    ? `${patientSocial?.smokePerDay}`
    : "",
  yearsSmoking: patientSocial?.exSmokerYears
    ? `${patientSocial?.exSmokerYears}`
    : "",
  drinker: patientSocial?.alcoholUnitsPerWeek ? "true" : "false",
  pulse: "",
  bp1:
    !isProstateReferral && !isLungsReferral
      ? baselineBloodPressure(patientBaseline).bp1
      : "",
  bp2:
    !isProstateReferral && !isLungsReferral
      ? baselineBloodPressure(patientBaseline).bp2
      : "",
  bodyHeight:
    !isProstateReferral && !isLungsReferral
      ? patientBaseline?.heightCm
        ? (patientBaseline?.heightCm / 100).toFixed(2)
        : ""
      : "",
  weight:
    !isProstateReferral && !isLungsReferral
      ? patientBaseline?.weightKg
        ? `${patientBaseline?.weightKg}`
        : ""
      : "",
  bmi:
    !isProstateReferral && !isLungsReferral
      ? patientBaseline?.heightCm && patientBaseline?.weightKg
        ? `${
            Number(patientBaseline?.weightKg) /
            Math.pow((Number(patientBaseline?.heightCm) / 100).toFixed(2), 2)
          }`
        : ""
      : "",
  clinicalExamination: "",
  laboratoryInvestigation: [],
  radiologyInvestigation: [],
  fileAttachment: [],
});
